import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Modal, ModalProps, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { CustomFileMap, FileNode, NullableFileNode, SelectedFile } from '../../../types/FileManagerTypes';
import FileManagerService from '../../../services/FileManager/fileManager.service';
import { SearchBar } from '../../../views/FileManager/components/SearchBar/SearchBar';
import ConditionalRender from '../ConditionalRender';
import Notification from '../Notification';
import { NotificationTypes } from '../../../enums/notificationTypes';
import { ROOT_DISPLAY_NAME } from '../../../views/FileManager/constants';
import { SHAPEFILE_EXTENSIONS, baseNameOf, extensionOf, fileTypeForExtension } from './constants';
import styles from './fileSelectorBrowser.module.scss';

export interface FileSelectorBrowserProps extends Omit<ModalProps, 'onCancel' | 'children' | 'footer' | 'title'> {
  open: boolean;
  onClose: () => void;
  /**
   * Called when the user confirms a file. Return `false` (sync or async) to
   * keep the modal open — used for client-side validation failures.
   */
  onSelect: (file: SelectedFile) => void | boolean | Promise<void | boolean>;
  /** Secondary button action; falls back to `onClose`. */
  onBack?: () => void;
  variant?: 'light' | 'dark';
  title?: string;
  /** Root crumb label. */
  rootLabel?: string;
  secondaryLabel?: string;
  primaryLabel?: string;
}

/** Stable identity so the derivation memos don't re-run on every render. */
const EMPTY_FILE_MAP: CustomFileMap = {};

const byName = (a: FileNode, b: FileNode) => a.name.localeCompare(b.name);

const isSelectableFile = (node: FileNode) => !node.isDir && Boolean(fileTypeForExtension(extensionOf(node.name)));

const INCOMPLETE_SHAPEFILE_MESSAGE = 'Select a complete Shapefile set with matching filenames (.shp, .shx, .dbf, .prj).';

/** A shapefile is only usable once all four parts are present. */
const isIncompleteShapefileSet = (file: FileNode, set: FileNode[]) =>
  SHAPEFILE_EXTENSIONS.has(extensionOf(file.name)) && new Set(set.map((node) => extensionOf(node.name))).size !== SHAPEFILE_EXTENSIONS.size;

const activateOnKey = (event: React.KeyboardEvent, activate: () => void) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  activate();
};

const Crumb: React.FC<{ label: string; onOpen: () => void }> = ({ label, onOpen }) => (
  <span role="button" tabIndex={0} className={styles.crumb} onClick={onOpen} onKeyDown={(event) => activateOnKey(event, onOpen)}>
    {label}
  </span>
);

const FileSelectorBrowser: React.FC<FileSelectorBrowserProps> = ({
  open,
  onClose,
  onSelect,
  onBack,
  variant = 'dark',
  title = 'UPLOAD FROM FILE MANAGER',
  rootLabel = ROOT_DISPLAY_NAME,
  secondaryLabel = 'BACK',
  primaryLabel = 'UPLOAD',
  ...modalProps
}) => {
  const [currentFolderId, setCurrentFolderId] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState<string>('');

  const { getFileMapping } = FileManagerService();
  // staleTime 0 (against the app-wide 30s default): the picker must list files
  // added since the last open.
  const { data, isFetching } = useQuery({
    ...getFileMapping(open),
    staleTime: 0,
  });

  const fileMap: CustomFileMap = data?.fileMap ?? EMPTY_FILE_MAP;
  const rootId = data?.rootFolderId ?? '';
  const folderId = currentFolderId || rootId;
  const searchTerm = searchInput.trim().toLowerCase();
  const isSearching = searchTerm.length > 0;

  // destroyOnClose tears down the Modal's DOM but not this component, so state
  // must be reset explicitly for the next open.
  useEffect(() => {
    if (open) return;
    setCurrentFolderId('');
    setSelectedIds([]);
    setSearchInput('');
  }, [open]);

  const childrenOf = (parentId: string): FileNode[] =>
    (fileMap[parentId]?.childrenIds ?? []).map((childId) => fileMap[childId]).filter((node): node is FileNode => Boolean(node));

  // A search spans every folder, so it yields a flat file list with no folder
  // rows to navigate.
  const { folders, files } = useMemo(() => {
    const folderRows: FileNode[] = [];
    const fileRows: FileNode[] = [];

    if (isSearching) {
      for (const node of Object.values(fileMap)) {
        if (node && node.name.toLowerCase().includes(searchTerm) && isSelectableFile(node)) {
          fileRows.push(node);
        }
      }
      return { folders: folderRows, files: fileRows.sort(byName) };
    }

    for (const childId of fileMap[folderId]?.childrenIds ?? []) {
      const node = fileMap[childId];
      if (!node) continue;
      if (node.isDir) folderRows.push(node);
      else if (isSelectableFile(node)) fileRows.push(node);
    }
    return { folders: folderRows.sort(byName), files: fileRows.sort(byName) };
  }, [fileMap, folderId, isSearching, searchTerm]);

  const breadcrumb = useMemo(() => {
    const chain: FileNode[] = [];
    const seen = new Set<string>();
    let cursor: string | undefined = folderId;
    while (cursor && cursor !== rootId && !seen.has(cursor)) {
      seen.add(cursor);
      const node: NullableFileNode = fileMap[cursor];
      if (!node) break;
      chain.unshift(node);
      cursor = node.parentId;
    }
    return chain;
  }, [fileMap, folderId, rootId]);

  /**
   * A shapefile's parts are matched on basename within its own folder, so a
   * second shapefile sitting alongside it is never pulled in.
   */
  const selectionSetFor = (file: FileNode): FileNode[] => {
    if (!SHAPEFILE_EXTENSIONS.has(extensionOf(file.name))) return [file];

    const base = baseNameOf(file.name).toLowerCase();
    const siblings = childrenOf(file.parentId ?? rootId).filter(
      (node) => !node.isDir && SHAPEFILE_EXTENSIONS.has(extensionOf(node.name)) && baseNameOf(node.name).toLowerCase() === base,
    );
    return siblings.length ? siblings.sort(byName) : [file];
  };

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  /** The .shp drives the layer for a set; otherwise there is only one file. */
  const { selectedFiles, primaryFile } = useMemo(() => {
    const nodes = selectedIds.map((id) => fileMap[id]).filter((node): node is FileNode => Boolean(node));
    return {
      selectedFiles: nodes,
      primaryFile: nodes.find((file) => extensionOf(file.name) === '.shp') ?? nodes[0],
    };
  }, [fileMap, selectedIds]);

  const handleFileClick = (file: FileNode) => {
    if (selectedIdSet.has(file.id)) {
      setSelectedIds([]);
      return;
    }

    const set = selectionSetFor(file);
    if (isIncompleteShapefileSet(file, set)) {
      Notification({ message: INCOMPLETE_SHAPEFILE_MESSAGE, type: NotificationTypes.ERROR });
      return;
    }

    setSelectedIds(set.map((node) => node.id));
  };

  const handleOpenFolder = (id: string) => {
    setSearchInput('');
    setCurrentFolderId(id);
  };

  const buildS3Key = (parentId: string): string => {
    const chain: string[] = [];
    const seen = new Set<string>();
    let cursor: string | undefined = parentId;
    while (cursor && cursor !== rootId && !seen.has(cursor)) {
      seen.add(cursor);
      chain.unshift(cursor);
      cursor = fileMap[cursor]?.parentId;
    }
    return [rootId, ...chain].join('/');
  };

  const handleConfirm = async () => {
    if (!primaryFile) return;

    const fileType = fileTypeForExtension(extensionOf(primaryFile.name));
    if (!fileType) return;

    const parentId = primaryFile.parentId ?? rootId;
    const result = await onSelect({
      id: primaryFile.id,
      name: primaryFile.name,
      baseName: baseNameOf(primaryFile.name),
      files: selectedFiles,
      folderId: parentId,
      folderName: fileMap[parentId]?.name ?? ROOT_DISPLAY_NAME,
      s3Key: buildS3Key(parentId),
      fileType,
    });
    if (result === false) return;
    onClose();
  };

  const handleBack = () => (onBack ?? onClose)();

  const renderRow = (node: FileNode, isFolder: boolean) => {
    const isSelected = selectedIdSet.has(node.id);
    const icon = isFolder ? '/assets/folder-dark-icon.svg' : isSelected ? '/assets/file-icon-teal.svg' : '/assets/file-icon-dark.svg';

    const activate = () => (isFolder ? handleOpenFolder(node.id) : handleFileClick(node));

    return (
      <div
        key={node.id}
        role="button"
        tabIndex={0}
        aria-pressed={isFolder ? undefined : isSelected}
        title={node.name}
        className={styles.row}
        onClick={activate}
        onKeyDown={(event) => activateOnKey(event, activate)}
      >
        <img className={styles.rowIcon} src={icon} alt="" />
        <span className={styles.rowName}>{node.name}</span>
        {isFolder ? null : (
          <Checkbox
            className={`custom-checkbox ${styles.rowCheckbox}`}
            checked={isSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => handleFileClick(node)}
          />
        )}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={handleBack}
      centered
      footer={null}
      closable={false}
      maskClosable={false}
      rootClassName={styles.fileModalRoot}
      title={null}
      className={styles.modal}
      width={480}
      destroyOnClose
      {...modalProps}
    >
      <div className={`${styles.container} ${styles[variant]}`}>
        <header className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
        </header>

        <div className={styles.body}>
          <ConditionalRender
            visible={!isFetching}
            fallback={
              <div className={styles.loader}>
                <Spin />
              </div>
            }
          >
            <div className={styles.listBox}>
              <SearchBar value={searchInput} onChange={setSearchInput} placeholder="SEARCH FOR FILES" />

              <ConditionalRender visible={!isSearching}>
                <div className={styles.breadcrumb} aria-label="Current folder">
                  <Crumb label={rootLabel} onOpen={() => handleOpenFolder(rootId)} />
                  {breadcrumb.map((node) => (
                    <React.Fragment key={node.id}>
                      <span className={styles.crumbSeparator}>&gt;</span>
                      <Crumb label={node.name} onOpen={() => handleOpenFolder(node.id)} />
                    </React.Fragment>
                  ))}
                </div>
              </ConditionalRender>

              <div className={styles.list} aria-label="Files">
                {folders.map((folder) => renderRow(folder, true))}
                {files.map((file) => renderRow(file, false))}
                {folders.length || files.length ? null : (
                  <p className={styles.empty}>{isSearching ? 'No matching files' : 'No files in this folder'}</p>
                )}
              </div>
            </div>
          </ConditionalRender>
        </div>

        <div className={styles.footer}>
          <Button className={styles.backBtn} onClick={handleBack}>
            {secondaryLabel}
          </Button>
          <div className={styles.selectedName} title={primaryFile?.name}>
            {primaryFile?.name ?? ''}
          </div>
          <Button className={styles.addBtn} disabled={!primaryFile} onClick={handleConfirm}>
            {primaryLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default FileSelectorBrowser;
